// Transportation types for the VoyagePlanner application

export interface Transportation {
  id: number;
  name: string;
  type: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  costPerKm?: string; // decimal in backend
  capacity?: number;
  features?: string;
  isActive: boolean;
  createdAt: string;
  // ── DAKO Telematik (per-transportation) – password is NEVER returned ──
  dakoCompanyIdent?: string | null;
  dakoVehicleIdent?: string | null;
  dakoUsername?: string | null;
  dakoPollIntervalSeconds?: number | null;
  dakoApiBaseUrl?: string | null;
  /** True when all DAKO credentials required to poll positions are present. */
  hasDakoConfig?: boolean;
}

export interface CreateTransportationRequest {
  name: string;
  type: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  costPerKm?: string; // decimal in backend
  capacity?: number;
  features?: string;
  isActive?: boolean;
  // ── DAKO Telematik (per-transportation) ──────────────────────────────
  dakoCompanyIdent?: string | null;
  dakoVehicleIdent?: string | null;
  dakoUsername?: string | null;
  dakoPassword?: string | null;
  dakoPollIntervalSeconds?: number | null;
  dakoApiBaseUrl?: string | null;
}

export interface UpdateTransportationRequest {
  name?: string;
  type?: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  costPerKm?: string; // decimal in backend
  capacity?: number;
  features?: string;
  isActive?: boolean;
  // ── DAKO Telematik (per-transportation) ──────────────────────────────
  dakoCompanyIdent?: string | null;
  dakoVehicleIdent?: string | null;
  dakoUsername?: string | null;
  dakoPassword?: string | null;
  dakoPollIntervalSeconds?: number | null;
  dakoApiBaseUrl?: string | null;
}

export interface TransportationResponse {
  id: number;
  name: string;
  type: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  costPerKm?: string; // decimal in backend
  capacity?: number;
  features?: string;
  isActive: boolean;
  createdAt: string;
  // ── DAKO Telematik (per-transportation) – password is NEVER returned ──
  dakoCompanyIdent?: string | null;
  dakoVehicleIdent?: string | null;
  dakoUsername?: string | null;
  dakoPollIntervalSeconds?: number | null;
  dakoApiBaseUrl?: string | null;
  /** True when all DAKO credentials required to poll positions are present. */
  hasDakoConfig?: boolean;
}

/** Result of a DAKO connection test for a transport method. */
export interface DakoTestConnectionResult {
  success: boolean;
  message: string;
  vehicleIdent: string;
}

/** Request to discover companies/vehicles available to a set of DAKO credentials. */
export interface DakoDiscoverRequest {
  username: string;
  password: string;
  companyIdent?: string | null;
  apiBaseUrl?: string | null;
}

/** A vehicle discovered via the DAKO API. */
export interface DakoDiscoveredVehicle {
  ident: string;
  licenceNumber?: string | null;
  alias?: string | null;
  externalName?: string | null;
  vin?: string | null;
  hasGpsTransmitter: boolean;
  isActive: boolean;
}

/** Result of a DAKO company/vehicle discovery request. */
export interface DakoDiscoveryResult {
  /** Company idents the credentials can access (populated when no company was supplied). */
  companies: string[];
  /** The resolved company ident whose vehicles were fetched (empty when a choice is needed). */
  selectedCompany: string;
  /** Vehicles belonging to the selected company. */
  vehicles: DakoDiscoveredVehicle[];
  /** German error message when discovery failed; undefined on success. */
  error?: string | null;
}

export interface TransportationTypeInfo {
  value: string;
  label: string;
  icon: string;
  color: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

// Transportation type constants
export const TRANSPORTATION_TYPES: TransportationTypeInfo[] = [
  { value: 'train', label: 'Zug', icon: '', color: '#10b981' },
  { value: 'plane', label: 'Flugzeug', icon: '', color: '#3b82f6' },
  { value: 'car', label: 'Auto', icon: '', color: '#ef4444' },
  { value: 'bus', label: 'Bus', icon: '', color: '#f59e0b' },
  { value: 'ship', label: 'Schiff', icon: '', color: '#06b6d4' },
  { value: 'bike', label: 'Fahrrad', icon: '', color: '#84cc16' },
  { value: 'walk', label: 'Zu Fuß', icon: '', color: '#6b7280' },
  { value: 'taxi', label: 'Taxi', icon: '', color: '#eab308' },
  { value: 'metro', label: 'U-Bahn', icon: '', color: '#8b5cf6' },
  { value: 'other', label: 'Sonstiges', icon: '', color: '#64748b' }
];

export const getTransportationTypeInfo = (type: string): TransportationTypeInfo => {
  return TRANSPORTATION_TYPES.find(t => t.value === type) || TRANSPORTATION_TYPES[TRANSPORTATION_TYPES.length - 1];
};
